import React, { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, Search, Clock, Zap, Database, AlertTriangle, CheckCircle, XCircle, Loader, Download } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { getLogs, getLogDates, listTenants, getAllLogsForExport } from '../../utils/adminApi';

function exportSessionsCSV(sessions, date, tenant, timeLabel) {
  const cols = [
    'Timestamp', 'Tenant', 'User', 'Query', 'Status',
    'Module', 'Service', 'Entity Set', 'Action',
    'Records', 'Total ms', 'LLM ms', 'SAP ms',
    'Tokens', 'Cost USD', 'Confidence', 'Filters', 'SAP URL', 'SAP Request URL', 'Error Details',
  ];
  const escape = v => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = sessions.map(s => [
    s.timestamp, s.tenant, s.user, s.query, s.status,
    s.module, s.service, s.entity_set, s.action,
    s.records, s.elapsed_ms, s.llm_elapsed_ms, s.sap_elapsed_ms,
    s.tokens, s.cost_usd != null ? s.cost_usd.toFixed(4) : '',
    s.confidence != null ? (s.confidence * 100).toFixed(0) + '%' : '',
    s.filters, s.sap_url, s.sap_request_url,
    s.error_detail || '',
  ].map(escape).join(','));

  const csv  = [cols.join(','), ...rows].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  const suffix = timeLabel && timeLabel !== 'All day' ? `-${timeLabel.replace(/\s+/g, '')}` : '';
  a.download = `querely-logs-${date}${tenant ? `-${tenant}` : ''}${suffix}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const TIME_RANGE_OPTIONS = [
  { value: 'all',    label: 'All day'    },
  { value: '5m',     label: 'Last 5 min' },
  { value: '10m',    label: 'Last 10 min'},
  { value: '30m',    label: 'Last 30 min'},
  { value: '1h',     label: 'Last 1 hr'  },
  { value: 'custom', label: 'Custom…'    },
];

function applyTimeFilter(sessions, timeRange, customFrom, customTo) {
  if (timeRange === 'all') return sessions;

  if (timeRange === 'custom') {
    return sessions.filter(s => {
      if (!s.timestamp) return true;
      const t = s.timestamp.slice(11, 16); // HH:MM
      return (!customFrom || t >= customFrom) && (!customTo || t <= customTo);
    });
  }

  const minutes = { '5m': 5, '10m': 10, '30m': 30, '1h': 60 }[timeRange] || 0;
  const cutoff  = new Date(Date.now() - minutes * 60 * 1000);
  return sessions.filter(s => {
    if (!s.timestamp) return true;
    return new Date(s.timestamp.replace(' ', 'T')) >= cutoff;
  });
}

// ── Logger badge colours ──────────────────────────────────────────────────────
const LOGGER_COLORS = {
  'cai-llm-service':    '#8b5cf6',
  'cai-sap-connector':  '#3b82f6',
  'cai-sap-metadata':   '#06b6d4',
  'cai-chat-router':    '#10b981',
  'cai-auth-service':   '#f59e0b',
  'cai-auth-router':    '#f59e0b',
  'cai-main':           '#6b7280',
  'cai-usage':          '#64748b',
};

const LEVEL_COLORS = {
  DEBUG:    '#94a3b8',
  INFO:     '#64748b',
  WARNING:  '#f59e0b',
  ERROR:    '#ef4444',
  CRITICAL: '#dc2626',
};

function loggerColor(name) {
  return LOGGER_COLORS[name] || '#94a3b8';
}

function levelColor(level) {
  return LEVEL_COLORS[level] || '#94a3b8';
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    success:     { icon: CheckCircle,    color: '#10b981', label: 'OK'       },
    error:       { icon: XCircle,        color: '#ef4444', label: 'Error'    },
    warning:     { icon: AlertTriangle,  color: '#f59e0b', label: 'Warning'  },
    in_progress: { icon: Loader,         color: '#3b82f6', label: 'Running'  },
    incomplete:  { icon: AlertTriangle,  color: '#94a3b8', label: 'Partial'  },
  };
  const cfg = map[status] || map.incomplete;
  const Icon = cfg.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: cfg.color, fontSize: '0.75rem', fontWeight: 600 }}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
}

// ── Query list item ───────────────────────────────────────────────────────────
function QueryListItem({ session, selected, onClick }) {
  const elapsed = session.elapsed_ms ? `${(session.elapsed_ms / 1000).toFixed(1)}s` : '—';
  const timeStr = session.timestamp ? session.timestamp.slice(11, 19) : '';

  return (
    <div
      onClick={onClick}
      style={{
        padding: '10px 14px',
        borderBottom: '1px solid var(--clr-border-soft)',
        cursor: 'pointer',
        background: selected ? 'var(--clr-accent-muted)' : 'transparent',
        borderLeft: selected ? '3px solid var(--clr-accent)' : '3px solid transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--clr-text-muted)', fontFamily: 'monospace' }}>{timeStr}</span>
        <StatusBadge status={session.status} />
      </div>
      <div style={{ fontSize: '0.82rem', color: 'var(--clr-text-primary)', marginBottom: 4, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {session.query || '(no query)'}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {session.module && (
          <span style={{ fontSize: '0.7rem', background: 'var(--clr-accent-muted)', color: 'var(--clr-accent-light)', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>
            {session.module}
          </span>
        )}
        {session.entity_set && (
          <span style={{ fontSize: '0.7rem', color: 'var(--clr-text-muted)' }}>{session.entity_set}</span>
        )}
        <span style={{ fontSize: '0.7rem', color: 'var(--clr-text-muted)', marginLeft: 'auto' }}>
          {session.records != null ? `${session.records} rec` : ''}{session.records != null && elapsed !== '—' ? ' · ' : ''}{elapsed}
        </span>
      </div>
    </div>
  );
}

// ── Event timeline row ────────────────────────────────────────────────────────
function EventRow({ event }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px solid var(--clr-border-soft)', alignItems: 'flex-start', fontSize: '0.78rem' }}>
      <span style={{ fontFamily: 'monospace', color: 'var(--clr-text-muted)', minWidth: 68, paddingTop: 1 }}>{event.time}</span>
      <span style={{
        minWidth: 8, height: 8, borderRadius: '50%', background: levelColor(event.level),
        marginTop: 5, flexShrink: 0
      }} />
      <span style={{
        fontSize: '0.68rem', padding: '1px 5px', borderRadius: 3,
        background: loggerColor(event.logger) + '22',
        color: loggerColor(event.logger),
        minWidth: 120, textAlign: 'center', flexShrink: 0,
      }}>
        {event.logger || '—'}
      </span>
      <span style={{
        color: LEVEL_COLORS[event.level] || 'var(--clr-text-secondary)',
        fontFamily: 'monospace',
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
        flex: 1,
      }}>
        {event.message}
      </span>
    </div>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────
function QueryDetail({ session }) {
  if (!session) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--clr-text-muted)', fontSize: '0.85rem' }}>
        Select a query to see details
      </div>
    );
  }

  const statBox = (icon, label, value, color) => (
    <div style={{ background: 'var(--clr-bg-elevated)', borderRadius: 8, padding: '10px 14px', minWidth: 110 }}>
      <div style={{ fontSize: '0.68rem', color: 'var(--clr-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: '1rem', fontWeight: 700, color: color || 'var(--clr-text-primary)' }}>{value ?? '—'}</div>
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div>
        <div style={{ fontSize: '0.72rem', color: 'var(--clr-text-muted)', marginBottom: 4 }}>
          {session.timestamp} · {session.tenant} · {session.user}
        </div>
        <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--clr-text-primary)', marginBottom: 8 }}>
          "{session.query}"
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {session.module && <span style={{ fontSize: '0.75rem', background: 'var(--clr-accent-muted)', color: 'var(--clr-accent-light)', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{session.module}</span>}
          {session.service && <span style={{ fontSize: '0.75rem', color: 'var(--clr-text-muted)' }}>{session.service}</span>}
          {session.entity_set && <span style={{ fontSize: '0.75rem', color: 'var(--clr-text-muted)' }}>→ {session.entity_set}</span>}
          {session.action && <span style={{ fontSize: '0.75rem', color: 'var(--clr-text-muted)' }}>({session.action})</span>}
        </div>
      </div>

      {/* SAP call details */}
      {(session.filters || session.select_fields || session.sap_url || session.sap_request_url) && (() => {
        // Prefer the exact, already-encoded URL SAP actually responded to
        // (captured at INFO level — works in production). Fall back to a
        // best-effort reconstruction for older log entries that predate this.
        const postmanUrl = session.sap_request_url || (() => {
          if (!session.sap_url) return null;
          const parts = [];
          if (session.filters)              parts.push(`$filter=${session.filters}`);
          if (session.select_fields?.length) parts.push(`$select=${session.select_fields.join(',')}`);
          parts.push('$format=json');
          return `${session.sap_url}?${parts.join('&')}`;
        })();
        return (
          <div style={{ background: 'var(--clr-bg-elevated)', borderRadius: 8, padding: '10px 14px', fontFamily: 'monospace', fontSize: '0.76rem' }}>
            {session.sap_url && <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--clr-text-muted)' }}>URL: </span><span style={{ color: '#3b82f6' }}>{session.sap_url}</span></div>}
            {session.sap_nav_url && <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--clr-text-muted)' }}>Nav: </span><span style={{ color: '#06b6d4' }}>{session.sap_nav_url}</span></div>}
            {session.filters && <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--clr-text-muted)' }}>$filter: </span><span style={{ color: '#10b981' }}>{session.filters}</span></div>}
            {session.select_fields?.length > 0 && <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--clr-text-muted)' }}>$select: </span><span style={{ color: '#f59e0b' }}>{session.select_fields.join(', ')}</span></div>}
            {postmanUrl && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--clr-border-soft)' }}>
                <span style={{ color: 'var(--clr-text-muted)' }}>Postman: </span>
                <span
                  title="Click to copy"
                  onClick={() => navigator.clipboard?.writeText(postmanUrl)}
                  style={{ color: '#a78bfa', cursor: 'pointer', wordBreak: 'break-all' }}
                >
                  {postmanUrl}
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {statBox(<Database size={12} />, 'Records', session.records)}
        {statBox(<Clock size={12} />, 'Total', session.elapsed_ms ? `${session.elapsed_ms}ms` : null)}
        {statBox(<Zap size={12} />, 'LLM', session.llm_elapsed_ms ? `${session.llm_elapsed_ms}ms` : null, '#8b5cf6')}
        {statBox(<Database size={12} />, 'SAP', session.sap_elapsed_ms ? `${session.sap_elapsed_ms}ms` : null, '#3b82f6')}
        {statBox(<Zap size={12} />, 'Tokens', session.tokens?.toLocaleString())}
        {session.cost_usd != null && statBox(<Zap size={12} />, 'Cost', `$${session.cost_usd.toFixed(4)}`)}
        {session.confidence != null && statBox(<CheckCircle size={12} />, 'Confidence', `${(session.confidence * 100).toFixed(0)}%`, session.confidence >= 0.9 ? '#10b981' : '#f59e0b')}
        {session.sap_http_status != null && statBox(<Database size={12} />, 'HTTP', session.sap_http_status, session.sap_http_status === 200 ? '#10b981' : '#ef4444')}
      </div>

      {/* Event timeline */}
      <div>
        <div style={{ fontSize: '0.72rem', color: 'var(--clr-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Event Timeline ({session.events?.length ?? 0} lines)
        </div>
        <div style={{ background: 'var(--clr-bg-surface)', borderRadius: 8, border: '1px solid var(--clr-border)', padding: '8px 12px', maxHeight: 380, overflowY: 'auto' }}>
          {(session.events || []).length === 0
            ? <span style={{ color: 'var(--clr-text-muted)', fontSize: '0.8rem' }}>No events</span>
            : (session.events || []).map((e, i) => <EventRow key={i} event={e} />)
          }
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function LogViewerPage() {
  const { adminKey }             = useAdminAuth();
  const [dates, setDates]        = useState([]);
  const [tenants, setTenants]    = useState([]);
  const [selectedDate, setDate]  = useState('');
  const [selectedTenant, setTen] = useState('');
  const [search, setSearch]      = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sessions, setSessions]  = useState([]);
  const [total, setTotal]        = useState(0);
  const [page, setPage]          = useState(1);
  const [pages, setPages]        = useState(1);
  const [loading, setLoading]        = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError]            = useState('');
  const [selected, setSelected]      = useState(null);
  const [timeRange, setTimeRange]    = useState('all');
  const [customFrom, setCustomFrom]  = useState('');
  const [customTo, setCustomTo]      = useState('');
  const searchTimer                  = useRef(null);

  const timeLabel = TIME_RANGE_OPTIONS.find(o => o.value === timeRange)?.label || 'All day';

  async function handleDownload() {
    if (!selectedDate) return;
    setDownloading(true);
    try {
      const all      = await getAllLogsForExport(adminKey, { date: selectedDate, tenant: selectedTenant || undefined, search: search || undefined });
      const filtered = applyTimeFilter(all, timeRange, customFrom, customTo);
      exportSessionsCSV(filtered, selectedDate, selectedTenant, timeRange !== 'all' ? timeLabel : '');
    } catch (e) {
      setError('Download failed: ' + e.message);
    } finally {
      setDownloading(false);
    }
  }

  // Load available dates and tenants on mount
  useEffect(() => {
    getLogDates(adminKey)
      .then(d => {
        const today = new Date().toISOString().split('T')[0];
        const fromApi = d.dates || [];
        const allDates = fromApi.includes(today) ? fromApi : [today, ...fromApi];
        setDates(allDates);
        setDate(allDates[0]);
      })
      .catch(() => {});
    listTenants(adminKey)
      .then(d => setTenants((Array.isArray(d) ? d : []).map(t => t.tenant_id).filter(Boolean)))
      .catch(() => {});
  }, [adminKey]);

  const load = useCallback((pg = 1) => {
    if (!selectedDate) return;
    setLoading(true);
    setError('');
    getLogs(adminKey, { date: selectedDate, tenant: selectedTenant || undefined, search: search || undefined, page: pg, pageSize: 20 })
      .then(data => {
        setSessions(data.sessions || []);
        setTotal(data.total || 0);
        setPage(data.page || 1);
        setPages(data.pages || 1);
        setSelected(null);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [adminKey, selectedDate, selectedTenant, search]);

  useEffect(() => { load(1); }, [load]);

  function handleSearchChange(val) {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(val), 400);
  }

  return (
    <div className="admin-page" style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '0 0 14px 0', borderBottom: '1px solid var(--clr-border-soft)' }}>
        <h2 style={{ margin: 0, flex: 'none', fontSize: '1.1rem', fontWeight: 700 }}>Log Viewer</h2>

        <select
          value={selectedDate}
          onChange={e => setDate(e.target.value)}
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-primary)', fontSize: '0.82rem' }}
        >
          {dates.map(d => <option key={d} value={d}>{d}</option>)}
          {dates.length === 0 && <option value="">No logs</option>}
        </select>

        <select
          value={selectedTenant}
          onChange={e => setTen(e.target.value)}
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-primary)', fontSize: '0.82rem' }}
        >
          <option value="">All tenants</option>
          {tenants.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--clr-bg-elevated)', border: '1px solid var(--clr-border)', borderRadius: 6, padding: '4px 10px', flex: 1, minWidth: 180, maxWidth: 320 }}>
          <Search size={13} style={{ color: 'var(--clr-text-muted)' }} />
          <input
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="Search queries…"
            style={{ border: 'none', background: 'transparent', outline: 'none', color: 'var(--clr-text-primary)', fontSize: '0.82rem', width: '100%' }}
          />
        </div>

        <button
          onClick={() => load(1)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-primary)', cursor: 'pointer', fontSize: '0.82rem' }}
        >
          <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
        </button>

        <button
          onClick={handleDownload}
          disabled={downloading || !selectedDate}
          title="Download all sessions for selected date as CSV"
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', color: downloading ? 'var(--clr-text-muted)' : 'var(--clr-text-primary)', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '0.82rem', opacity: downloading ? 0.6 : 1 }}
        >
          <Download size={13} /> {downloading ? 'Downloading…' : 'Download CSV'}
        </button>

        <select
          value={timeRange}
          onChange={e => { setTimeRange(e.target.value); setSelected(null); }}
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-primary)', fontSize: '0.82rem' }}
        >
          {TIME_RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {timeRange === 'custom' && (
          <>
            <input
              type="time"
              value={customFrom}
              onChange={e => { setCustomFrom(e.target.value); setSelected(null); }}
              title="From time"
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-primary)', fontSize: '0.82rem' }}
            />
            <span style={{ color: 'var(--clr-text-muted)', fontSize: '0.82rem' }}>–</span>
            <input
              type="time"
              value={customTo}
              onChange={e => { setCustomTo(e.target.value); setSelected(null); }}
              title="To time"
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-primary)', fontSize: '0.82rem' }}
            />
          </>
        )}

        <span style={{ fontSize: '0.75rem', color: 'var(--clr-text-muted)', marginLeft: 'auto' }}>
          {total} session{total !== 1 ? 's' : ''}
        </span>
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: '0.85rem', padding: '8px 0' }}>{error}</div>}

      {/* Two-panel body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 0, marginTop: 12 }}>

        {/* Left — query list */}
        {(() => {
          const visibleSessions = applyTimeFilter(sessions, timeRange, customFrom, customTo);
          return (<>
            <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', border: '1px solid var(--clr-border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {loading && sessions.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 24, color: 'var(--clr-text-muted)', fontSize: '0.85rem' }}>Loading…</div>
                )}
                {!loading && visibleSessions.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 24, color: 'var(--clr-text-muted)', fontSize: '0.85rem' }}>
                    {sessions.length > 0 ? 'No sessions in selected time range' : 'No sessions found'}
                  </div>
                )}
                {visibleSessions.map((s, i) => (
                  <QueryListItem
                    key={i}
                    session={s}
                    selected={selected === i}
                    onClick={() => setSelected(selected === i ? null : i)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '8px', borderTop: '1px solid var(--clr-border-soft)' }}>
                  <button disabled={page <= 1} onClick={() => load(page - 1)} style={{ padding: '3px 10px', borderRadius: 4, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', cursor: page > 1 ? 'pointer' : 'not-allowed', opacity: page <= 1 ? 0.4 : 1, fontSize: '0.78rem' }}>‹</button>
                  <span style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)', alignSelf: 'center' }}>{page} / {pages}</span>
                  <button disabled={page >= pages} onClick={() => load(page + 1)} style={{ padding: '3px 10px', borderRadius: 4, border: '1px solid var(--clr-border)', background: 'var(--clr-bg-elevated)', cursor: page < pages ? 'pointer' : 'not-allowed', opacity: page >= pages ? 0.4 : 1, fontSize: '0.78rem' }}>›</button>
                </div>
              )}
            </div>

            {/* Right — detail */}
            <div style={{ flex: 1, marginLeft: 12, border: '1px solid var(--clr-border)', borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <QueryDetail session={selected !== null ? visibleSessions[selected] : null} />
            </div>
          </>);
        })()}
      </div>
    </div>
  );
}
